"use client"

import { useState, useEffect } from "react"
import Link from "next/link"
import { useRouter, usePathname } from "next/navigation"
import { Button } from "@/components/ui/button"
import { BookOpen, LogOut, Menu } from "lucide-react"
import { useSupabase } from "@/lib/supabase/context"
import { logAuditEvent } from "@/lib/utils/audit-logger"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"

export function Header() {
  const [user, setUser] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const supabase = useSupabase()
  const router = useRouter()
  const pathname = usePathname()

  useEffect(() => {
    const loadUser = async () => {
      try {
        const {
          data: { user },
          error,
        } = await supabase.auth.getUser()
        
        // Silently handle missing sessions - completely normal for unauthenticated users
        if (error) {
          const errorMessage = error.message || ''
          if (errorMessage.includes('Invalid Refresh Token') || 
              errorMessage.includes('Refresh Token Not Found') ||
              errorMessage.includes('refresh token') ||
              errorMessage.includes('Auth session missing') ||
              errorMessage.includes('AuthSessionMissingError')) {
            setUser(null)
            setIsLoading(false)
            return
          }
        }
        
        setUser(user || null)
      } catch (error: any) {
        // Silently handle expected "no session" errors
        const errorMessage = error?.message || ''
        if (errorMessage.includes('Invalid Refresh Token') || 
            errorMessage.includes('Refresh Token Not Found') ||
            errorMessage.includes('refresh token') ||
            errorMessage.includes('Auth session missing') ||
            errorMessage.includes('AuthSessionMissingError')) {
          setUser(null)
        }
      } finally {
        setIsLoading(false)
      }
    }
    loadUser()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleLogout = async () => {
    // Log logout before signing out (user still exists in session)
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (user) {
        await logAuditEvent({
          action: 'user_logout',
          resourceType: 'user',
          resourceId: user.id,
          metadata: { email: user.email }
        })
      }
    } catch (error) {
      // Fail silently - audit logging should not break logout
      console.error('Failed to log logout event:', error)
    }
    
    await supabase.auth.signOut()
    setUser(null)
    setIsMobileMenuOpen(false)
    router.refresh()
  }

  // Renders the label with an invisible bold copy stacked underneath so the
  // element always reserves the width of the bold (active) state. This keeps the
  // active item's font-medium from shifting neighboring nav items.
  const NavLabel = ({ children }: { children: string }) => (
    <span className="grid">
      <span aria-hidden className="col-start-1 row-start-1 font-medium invisible">{children}</span>
      <span className="col-start-1 row-start-1">{children}</span>
    </span>
  )

  const NavigationLinks = ({ isMobile = false }) => (
    <>
      <Link href="/yearly-reflect"
        className={`text-sm transition-colors ${pathname === "/yearly-reflect" ? "text-foreground font-medium" : "text-muted-foreground hover:text-foreground"} ${isMobile ? 'py-2 block' : ''}`}
        onClick={() => setIsMobileMenuOpen(false)}
        >
          <NavLabel>Year End Reflection</NavLabel>
      </Link>
      <Link
        href="/#features"
        className={`text-sm transition-colors ${pathname === "/" ? "text-foreground font-medium" : "text-muted-foreground hover:text-foreground"} ${isMobile ? 'py-2 block' : ''}`}
        onClick={() => setIsMobileMenuOpen(false)}
      >
        <NavLabel>Features</NavLabel>
      </Link>
      <Link
        href="/#pricing"
        className={`text-sm transition-colors ${pathname === "/" ? "text-foreground font-medium" : "text-muted-foreground hover:text-foreground"} ${isMobile ? 'py-2 block' : ''}`}
        onClick={() => setIsMobileMenuOpen(false)}
      >
        <NavLabel>Pricing</NavLabel>
      </Link>
      <Link
        href="/blog"
        className={`text-sm transition-colors ${pathname === "/blog" ? "text-foreground font-medium" : "text-muted-foreground hover:text-foreground"} ${isMobile ? 'py-2 block' : ''}`}
        onClick={() => setIsMobileMenuOpen(false)}
      >
        <NavLabel>Blog</NavLabel>
      </Link>
    </>
  )

  const AuthButtons = () => (
    user ? (
      <>
        <Link href="/planner" onClick={() => setIsMobileMenuOpen(false)}>
          <Button size="sm" className="w-full">Go to Planners</Button>
        </Link>
        <Button variant="ghost" size="sm" onClick={handleLogout} className="w-auto justify-start text-muted-foreground hover:text-foreground">
          <LogOut className="h-4 w-4 mr-2" />
          Logout
        </Button>
      </>
    ) : (
      <>
        <Link href="/auth/login" onClick={() => setIsMobileMenuOpen(false)}>
          <Button variant="ghost" size="sm" className="w-full">
            Sign In
          </Button>
        </Link>
        <Link href="/auth/sign-up" onClick={() => setIsMobileMenuOpen(false)}>
          <Button size="sm" className="w-full">Get Started</Button>
        </Link>
      </>
    )
  )

  return (
    <header className="border-b bg-card/50 backdrop-blur-sm sticky top-0 z-50">
      <div className="w-full px-4 py-4 md:container md:mx-auto flex items-center justify-between">
        <Link href="/" className="flex items-center gap-2">
          <BookOpen className="h-6 w-6 text-primary" />
          <span className="font-semibold text-xl">Planami</span>
        </Link>
        
        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-6">
          <NavigationLinks isMobile={false} />
          <AuthButtons />
        </nav>

        {/* Mobile Menu */}
        <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
          <SheetTrigger asChild className="md:hidden">
            <Button variant="ghost" size="sm" className="p-2">
              <Menu className="h-5 w-5" />
              <span className="sr-only">Open menu</span>
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[85vw] sm:w-[400px]">
            <SheetHeader>
              <SheetTitle className="text-left">Menu</SheetTitle>
            </SheetHeader>
            <nav className="flex flex-col gap-4 mt-6">
              <div className="flex flex-col gap-1">
                <NavigationLinks isMobile={true} />
              </div>
              <div className="border-t pt-4 flex flex-col gap-3">
                <AuthButtons />
              </div>
            </nav>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  )
}

